import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dropdown,
  DropdownItem,
  Link,
  Text,
  ThemeProvider,
} from '@amzn/storm-ui';
import { selectSignOutInfo, UserInfo } from '../../../header/store/slice';
import {
  InvertedMobileDivider,
  MobileLanguageSwitchBtn,
  MobileLanguageSwitcher,
  SecondaryMenuWrapper,
  UserPreferenceButtonLink,
} from './styles';
import handleLanguageSwitch from 'src/features/header/components/LanguageSwitcher/handle-language-switch';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import ThemeModesControl from 'src/features/header/components/ThemeModesControl/ThemeModesControl';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { HeaderBundleProvider } from 'src/providers/PantherLocalizationProvider';

const UserPreferenceView: React.VFC<UserInfo> = (userInfo) => {
  const initialLanguage = userInfo.languageInfo.currentLocale;
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const signOutInfo = useSelector(selectSignOutInfo);

  const isChromeThemeModesControlEnabled = useIsFeatureEnabled(
    WeblabedFeatures.CHROME_THEME_MODES_CONTROL,
  );

  const LanguageSwitcherOptions = userInfo.languageInfo.languageOptions.map(
    (language) => (
      <DropdownItem value={language.locale} key={language.locale}>
        {language.label}
      </DropdownItem>
    ),
  );

  return (
    <>
      <SecondaryMenuWrapper>
        <Text color="secondary" size="large">
          {userInfo.signedInAsLabel}
        </Text>
        <Text size="large">
          <strong>{userInfo.customerInfo?.fullName || ''}</strong>
        </Text>
        <Text color="secondary" size="large">
          {userInfo.customerInfo?.primaryEmail || ''}
        </Text>
      </SecondaryMenuWrapper>
      <InvertedMobileDivider />
      <SecondaryMenuWrapper>
        {userInfo.switchAccountInfo?.url && (
          <UserPreferenceButtonLink
            href={userInfo.switchAccountInfo?.url}
            {...getTaktAttributes('switch-account-link', chromeComponents.NAV, {
              url: userInfo.switchAccountInfo?.url,
            })}
          >
            {userInfo.switchAccountInfo?.label}
          </UserPreferenceButtonLink>
        )}
        <UserPreferenceButtonLink
          href={userInfo.signOutInfo.url}
          {...getTaktAttributes('sign-out', chromeComponents.NAV, {
            url: userInfo.signOutInfo?.url,
          })}
        >
          {userInfo.signOutInfo.label}
        </UserPreferenceButtonLink>
      </SecondaryMenuWrapper>
      <InvertedMobileDivider />
      {userInfo.notificationSettingsInfo && (
        <>
          <SecondaryMenuWrapper>
            <Link
              href={userInfo.notificationSettingsInfo.url}
              {...getTaktAttributes(
                'notification-settings',
                chromeComponents.NAV,
                { url: userInfo.notificationSettingsInfo?.url },
              )}
            >
              {userInfo.notificationSettingsInfo.label}
            </Link>
          </SecondaryMenuWrapper>
          <InvertedMobileDivider />
        </>
      )}
      <SecondaryMenuWrapper>
        <MobileLanguageSwitcher>
          <Dropdown
            label={userInfo.languageInfo.label}
            onChange={(language) => setSelectedLanguage(language)}
            selectedValue={selectedLanguage}
            fullWidth={true}
            {...getTaktAttributes(
              'aac-select-language',
              chromeComponents.NAV,
              userInfo.switchAccountInfo?.url
                ? {
                    url: userInfo.switchAccountInfo?.url,
                  }
                : {},
            )}
          >
            {LanguageSwitcherOptions}
          </Dropdown>
          {initialLanguage != selectedLanguage && (
            <MobileLanguageSwitchBtn
              variant="primary"
              onClick={async () =>
                await handleLanguageSwitch(
                  initialLanguage,
                  selectedLanguage,
                  signOutInfo.url,
                )
              }
              {...getTaktAttributes(
                'aac-language-switch-btn',
                chromeComponents.NAV,
              )}
            >
              {userInfo.languageInfo.changeLanguageText}
            </MobileLanguageSwitchBtn>
          )}
        </MobileLanguageSwitcher>
      </SecondaryMenuWrapper>
      {isChromeThemeModesControlEnabled && (
        <HeaderBundleProvider>
          <InvertedMobileDivider />
          <SecondaryMenuWrapper>
            <ThemeProvider enableThemeModes>
              <ThemeModesControl />
            </ThemeProvider>
          </SecondaryMenuWrapper>
        </HeaderBundleProvider>
      )}
    </>
  );
};

export default UserPreferenceView;
